import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import { Box, CircularProgress, IconButton, Tab, Tabs, Typography } from '@mui/material';
import * as subMatFolderActions from '../../store/subMatFolder';
import { createPreviewObjectUrl } from '../G04docuSubMatFolder/SubMatFolderFileActions';
import { tocDisplayTitle } from './subMatTemplateTocOrder';

export const useSubMatTocDocPreview = (items, active) => {
  const dispatch = useDispatch();
  const [pane, setPane] = useState('toc');
  const [doc, setDoc] = useState(null);
  const [loadingId, setLoadingId] = useState('');
  const [error, setError] = useState('');
  const cacheRef = useRef(new Map());
  const reqIdRef = useRef(0);

  const clearCache = useCallback(() => {
    cacheRef.current.forEach((entry) => {
      if (entry?.url) window.URL.revokeObjectURL(entry.url);
    });
    cacheRef.current = new Map();
  }, []);

  useEffect(() => () => {
    cacheRef.current.forEach((entry) => {
      if (entry?.url) window.URL.revokeObjectURL(entry.url);
    });
  }, []);

  useEffect(() => {
    if (active) return undefined;
    clearCache();
    setDoc(null);
    setPane('toc');
    setLoadingId('');
    setError('');
    return undefined;
  }, [active, clearCache]);

  useEffect(() => {
    if (!doc?.itemId) return;
    if ((items || []).some((it) => it.id === doc.itemId)) return;
    setDoc(null);
    setPane('toc');
  }, [items, doc]);

  const previewItem = useCallback(async (item) => {
    if (!item?.folderId) return;
    const reqId = reqIdRef.current + 1;
    reqIdRef.current = reqId;
    setError('');
    const title = tocDisplayTitle(item) || item.folderName || '서류';
    const cacheKey = item.folderId;
    const cached = cacheRef.current.get(cacheKey);
    if (cached?.url) {
      setDoc({ itemId: item.id, title, url: cached.url });
      setPane('doc');
      setLoadingId('');
      return;
    }
    setLoadingId(item.id);
    setDoc({ itemId: item.id, title, url: '' });
    setPane('doc');
    try {
      const files = await dispatch(subMatFolderActions.selectFiles(item.folderId));
      const current = (files || []).find((f) => f.isCurrent) || (files || [])[0];
      if (!current?.documentPath) {
        throw new Error('현재 사용 중인 PDF가 없습니다.');
      }
      const url = await createPreviewObjectUrl(current.documentPath);
      const prev = cacheRef.current.get(cacheKey);
      if (prev?.url && prev.url !== url) window.URL.revokeObjectURL(prev.url);
      cacheRef.current.set(cacheKey, { url, title });
      if (reqId !== reqIdRef.current) return;
      setDoc({ itemId: item.id, title, url });
    } catch (err) {
      if (reqId !== reqIdRef.current) return;
      setError(err?.message || '파일을 열 수 없습니다.');
    } finally {
      if (reqId === reqIdRef.current) setLoadingId('');
    }
  }, [dispatch]);

  const closeDoc = useCallback(() => {
    reqIdRef.current += 1;
    setPane('toc');
    setDoc(null);
    setLoadingId('');
    setError('');
  }, []);

  return {
    pane,
    setPane,
    doc,
    loadingId,
    error,
    previewItem,
    closeDoc,
  };
};

export const SubMatTemplateTocPreviewTitle = ({ pane, onPane, docTitle, onCloseDoc }) => {
  const value = pane === 'doc' && docTitle ? 'doc' : 'toc';
  return (
    <Tabs
      value={value}
      onChange={(_, next) => onPane(next)}
      variant="scrollable"
      scrollButtons="auto"
      aria-label="미리보기"
      sx={{
        minHeight: 40,
        my: -0.5,
        minWidth: 0,
        '& .MuiTabs-flexContainer': { minHeight: 40 },
        '& .MuiTab-root': {
          minHeight: 40,
          minWidth: 0,
          py: 0,
          px: 1.5,
          textTransform: 'none',
          fontSize: '0.8125rem',
          fontWeight: 600,
          maxWidth: 240,
        },
        '& .MuiTabs-indicator': { height: 2 },
      }}
    >
      <Tab value="toc" label="목차 미리보기" />
      {docTitle ? (
        <Tab
          value="doc"
          label={(
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0, maxWidth: 200 }}>
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {docTitle}
              </Box>
              <IconButton
                size="small"
                aria-label="서류 미리보기 닫기"
                component="span"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onCloseDoc?.();
                }}
                sx={{ p: 0.125, ml: -0.25, flexShrink: 0 }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          )}
        />
      ) : null}
    </Tabs>
  );
};

export const SubMatTemplateTocDocFrame = ({ url, error, loading }) => {
  const [iframeLoading, setIframeLoading] = useState(true);

  useEffect(() => {
    setIframeLoading(true);
  }, [url]);

  if (loading && !url) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error && !url) {
    return (
      <Box sx={{ p: 2, bgcolor: 'background.paper', flex: 1, minHeight: 0 }}>
        <Typography color="error" variant="body2">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, position: 'relative', bgcolor: 'background.paper' }}>
      {(iframeLoading || loading) && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      )}
      {error ? (
        <Typography color="error" variant="body2" sx={{ position: 'absolute', top: 8, left: 8, zIndex: 2 }}>
          {error}
        </Typography>
      ) : null}
      {url ? (
        <iframe
          title="서류 미리보기"
          src={url}
          onLoad={() => setIframeLoading(false)}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            opacity: iframeLoading ? 0.5 : 1,
          }}
        />
      ) : null}
    </Box>
  );
};
